import { S3_BRAND_MEDIA_BUCKET_LINK } from 'constants/links';
import { AutoBrandFont, querySite, updateBrand } from 'libs/e1api/autoBrand';
import { BrandColorList, BrandMetadata, BrandState, LogoMetadata } from 'modules/User';
import { toHex } from 'utils/color';

export type AutoBrandEntity = {
  colors: BrandColorList;
  logos: Blob[];
  fonts: AutoBrandFont[];
  suggestedFonts: string;
  metadata?: BrandMetadata;
};

/**
 * Helper to parse and normalize data from brand
 * @param brandURL string
 * @returns AutoBrandEntity
 */
export const getAutoBrand = async (brandURL: string, useFontFallback?: boolean): Promise<AutoBrandEntity> => {
  try {
    const { logos, colors, fonts, suggestedFonts = '' } = await querySite(brandURL, useFontFallback);
    let logoBlobs: Blob[] = [];
    let palettes: BrandColorList = [];

    if (colors.length === 0 && logos.length === 0) {
      return { logos: [], colors: [], fonts: [], suggestedFonts: '' };
    }

    try {
      logoBlobs = await Promise.all(logos.map((logo) => fetch(logo).then((res) => res.blob())));
    } catch (err) {
      console.error(err);
    }

    palettes = colors.map((color) => ({
      color: toHex(`rgb(${color[0]}, ${color[1]}, ${color[2]})`),
      label: '',
    }));

    return { logos: logoBlobs, colors: palettes, fonts, suggestedFonts };
  } catch (error) {
    console.error('getAutoBrand', error);
    return { logos: [], colors: [], fonts: [], suggestedFonts: '' };
  }
};

/**
 * Helper to check if the auto brand fonts response has fonts (that we support)
 *
 * @param brandFonts AutoBrandFont[]
 * @returns boolean
 */
export const hasBrandFonts = (brandFonts?: AutoBrandFont[]) => {
  if (!brandFonts) return false;
  if (!Array.isArray(brandFonts)) return false;
  if (!brandFonts.length) return false;

  // check if there is at least one font that is venngage font
  return brandFonts.some((f) => f?.isVenngageFont);
};

/**
 * Helper to create a mixpanel event object for auto brand fonts
 *
 * @param fonts AutoBrandFont[]
 * @returns object
 */
export const createMixpanelAutoFontEvent = (fonts: AutoBrandFont[]) => {
  const mixpanelFont: {
    'fonts found': number;
    'brand fonts': string[] | string;
    'unavailable google fonts': string[] | string;
  } = { 'fonts found': 0, 'brand fonts': [], 'unavailable google fonts': [] };

  fonts.forEach((font) => {
    if (font && font.fontFamily) {
      if ((font.type === 'title' || font.type === 'text') && font.isVenngageFont) {
        mixpanelFont['fonts found']++;
        (mixpanelFont['brand fonts'] as string[]).push(font.fontFamily);
      }
      if (font.origin === 'google' && !font.isVenngageFont) {
        (mixpanelFont['unavailable google fonts'] as string[]).push(font.fontFamily);
      }
    }
  });

  // Convert arrays to comma-separated strings
  mixpanelFont['brand fonts'] = (mixpanelFont['brand fonts'] as string[]).join(', ');
  mixpanelFont['unavailable google fonts'] = (mixpanelFont['unavailable google fonts'] as string[]).join(', ');

  return mixpanelFont;
};

export const updateBrandFonts = async (brand: BrandState, fontName: string) => {
  try {
    const fontSize: Record<string, number> = {
      title: 50,
      subtitle: 30,
      text: 20,
    };
    const payload: Record<string, any> = {};
    for (const type of ['title', 'subtitle', 'text']) {
      payload[type] = {
        fontFamily: fontName,
        bold: false,
        underline: false,
        italic: false,
        fontSize: fontSize[type],
      };
    }

    const response = await updateBrand({
      name: brand.name,
      id: brand.id,
      ...payload,
    });

    if (response?.id) {
      return {
        success: true,
      };
    }

    return {
      success: false,
    };
  } catch (error) {
    console.log('updateBrandFonts', error);

    return {
      success: false,
    };
  }
};

const brightnessFactorR = 0.299;
const brightnessFactorG = 0.587;
const brightnessFactorB = 0.114;

const getMeta = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = url;
    img.src = `${url}?not-from-cache`;
  });

const isImageDark = (image: HTMLImageElement) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = image.width;
    canvas.height = image.height;
    context?.drawImage(image, 0, 0);

    const imageData = context?.getImageData(0, 0, canvas.width, canvas.height);
    if (!imageData) return false;
    const data = imageData.data;

    let totalPixels = 0;
    let totalBrightness = 0;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      // Adjust alpha threshold as needed
      if (a > 127) {
        totalPixels++;

        const brightness = Math.sqrt(
          brightnessFactorR * Math.pow(r, 2) + brightnessFactorG * Math.pow(g, 2) + brightnessFactorB * Math.pow(b, 2),
        );
        totalBrightness += brightness;
      }
    }

    if (totalPixels === 0) {
      return false; // Image is entirely transparent
    }

    const averageBrightness = totalBrightness / totalPixels;
    const brightnessThreshold = 190; // Adjust threshold as needed - remember to adjust this value on assets as well

    resolve(averageBrightness < brightnessThreshold);
  });
};

/**
 * Checks if the image at the given url is dark or not, and returns an object with its darkness status,
 * the given logo url, and the image width and height.
 *
 * @param {string} url The url of the image to check.
 * @param {string} logo The url of the logo.
 * @returns {LogoMetadata} A promise that resolves with an object containing the image's darkness status,
 * the given logo url, and the image width and height.
 */
const checkImageIsDark = async (url: string, logo: string) => {
  const image = await getMeta(url);
  const isDark = await isImageDark(image);

  return {
    isDark,
    url: logo,
    width: image?.width,
    height: image?.height,
  } as LogoMetadata;
};

export async function getBrandLogos({
  metadataLogos = [],
  logos = [],
}: {
  metadataLogos?: LogoMetadata[];
  logos?: string[];
}) {
  return new Promise<LogoMetadata[]>(async (resolve, reject) => {
    try {
      let metaDataLogosList: LogoMetadata[] = [];
      // no logos, remove all metadata logos
      if (!logos || logos.length === 0) return resolve(metaDataLogosList);

      // no metadata logos add all logos
      if (!metadataLogos || metadataLogos.length === 0) {
        await Promise.all(logos.map((logo) => checkImageIsDark(`${S3_BRAND_MEDIA_BUCKET_LINK}/${logo}`, logo))).then(
          (logos) => {
            metaDataLogosList = logos;
          },
        );

        return resolve(metaDataLogosList);
      }

      // removing a logo
      metaDataLogosList = metadataLogos.filter((l) => logos.includes(l.url || ''));

      // adding a logo
      await Promise.all(
        logos
          .filter((logo) => !metadataLogos.find((l) => l.url === logo))
          .map((logo) => checkImageIsDark(`${S3_BRAND_MEDIA_BUCKET_LINK}/${logo}`, logo)),
      ).then((logos) => {
        metaDataLogosList = [...metaDataLogosList, ...logos];
      });

      return resolve(metaDataLogosList);
    } catch (error) {
      console.error('getBrandLogos', error);
      return reject([]);
    }
  });
}
